import React, { useEffect, useState } from "react";
import Layout from "../layout/index";
import axios from "axios";
import Swal from "sweetalert2";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { ROOT_API } from "../../api/config";
import { ErrorCode } from "../../api/ErrorCode";
import { formatDate } from "../../utils/dates";
import "./style.scss";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

const euro = (minor, currency) =>
  `${(minor / 100).toLocaleString("hr-HR", { minimumFractionDigits: 2 })} ${
    currency || "EUR"
  }`;

const Premium = () => {
  const intl = useIntl();
  const selectedLocale =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";
  const locale = selectedLocale === "en" ? "en-US" : "hr-HR";

  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [startingPlan, setStartingPlan] = useState(null);
  const [creditQty, setCreditQty] = useState(20);
  const [buyingCredits, setBuyingCredits] = useState(false);
  const [code, setCode] = useState(""); // promo / referral code (optional)

  // Shared checkout error. 503 = billing off; 429 = throttled; 400 = a specific catalog code
  // (invalid_code / code_already_used) resolved via ErrorCode; else a generic message.
  const showCheckoutError = (error) => {
    const st = error?.response?.status;
    const ec = error?.response?.data?.error_code;
    const soft = st === 503 || st === 429;
    let text;
    if (st === 503) text = intl.formatMessage({ id: "premium_unavailable" });
    else if (st === 429) text = intl.formatMessage({ id: "too_many_requests" });
    else if (ec && ErrorCode[ec]) text = intl.formatMessage({ id: ErrorCode[ec] });
    else text = intl.formatMessage({ id: "premium_error" });
    Swal.fire({
      icon: soft ? "info" : "error",
      title: soft ? intl.formatMessage({ id: "premium_unavailable_title" }) : "Oops...",
      text,
      confirmButtonText: "OK",
    });
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${ROOT_API}apis/billing/status`, {
          headers: authHeaders(),
        });
        if (alive) setStatus(res?.data?.data || null);
      } catch (e) {
        if (alive) setStatus(null);
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const isPremium = status?.premium;
  const currentPlan = status?.plan; // free | premium | business
  const currency = status?.currency || "EUR";
  const plans = status?.plans || [
    { key: "premium", price_minor: 2499, months: 12, addresses: 5, sms: 30 },
    { key: "business", price_minor: 6249, months: 12, addresses: 15, sms: 70 },
  ];

  const PLAN_NAME = { premium: "Premium", business: "Business" };

  const planFeatures = (plan) => {
    const feats = [
      intl.formatMessage({ id: "premium_feat_addr" }, { n: plan.addresses }),
      intl.formatMessage({ id: "premium_feat_instant" }),
      intl.formatMessage({ id: "premium_feat_channels" }),
      intl.formatMessage(
        { id: "premium_feat_sms" },
        { n: plan.sms ?? (plan.key === "business" ? 70 : 30) }
      ),
    ];
    if (plan.key === "business") {
      feats.push(intl.formatMessage({ id: "business_feat_support" }));
    }
    return feats;
  };

  const ENT_NAME = {
    enterprise: "Enterprise",
    enterprise_concierge: "Enterprise Concierge",
  };

  const entFeatures = (plan) => {
    const feats = [
      intl.formatMessage({ id: "premium_ent_feat_addr" }),
      intl.formatMessage({ id: "premium_feat_sms" }, { n: plan.sms || 730 }),
      intl.formatMessage({ id: "premium_ent_feat_digest" }),
      intl.formatMessage({ id: "premium_ent_feat_photos" }),
      intl.formatMessage({ id: "business_feat_support" }),
    ];
    if (plan.key === "enterprise_concierge") {
      feats.push(intl.formatMessage({ id: "premium_ent_feat_concierge" }));
    }
    return feats;
  };

  const handleUpgrade = async (planKey) => {
    if (startingPlan) return;
    try {
      setStartingPlan(planKey);
      const res = await axios.post(
        `${ROOT_API}apis/billing/create-order`,
        { plan: planKey, code: code.trim() || undefined },
        { headers: authHeaders() }
      );
      // Remember which order this is so the return page confirms THIS exact order (not just the
      // latest pending one) — see billing.confirm.
      const orderId = res?.data?.data?.order_id;
      if (orderId) localStorage.setItem("pending_order_id", orderId);
      const checkoutUrl = res?.data?.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl; // Revolut hosted checkout
        return;
      }
      throw new Error("no checkout url");
    } catch (error) {
      showCheckoutError(error);
    } finally {
      setStartingPlan(null);
    }
  };

  const buyCredits = async () => {
    const qty = Math.max(1, Math.min(parseInt(creditQty, 10) || 0, 1000));
    if (buyingCredits) return;
    try {
      setBuyingCredits(true);
      const res = await axios.post(
        `${ROOT_API}apis/billing/create-order`,
        { kind: "credits", sms_quantity: qty, code: code.trim() || undefined },
        { headers: authHeaders() }
      );
      const orderId = res?.data?.data?.order_id;
      if (orderId) localStorage.setItem("pending_order_id", orderId);
      const checkoutUrl = res?.data?.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      throw new Error("no checkout url");
    } catch (error) {
      showCheckoutError(error);
    } finally {
      setBuyingCredits(false);
    }
  };

  const PlanCard = ({ plan }) => {
    const highlight = plan.key === "business";
    const isCurrent = currentPlan === plan.key;
    return (
      <article className={`premium__plan${highlight ? " premium__plan--pro" : ""}`}>
        {highlight ? (
          <span className="premium__badge">
            {intl.formatMessage({ id: "business_badge" })}
          </span>
        ) : null}
        <h2 className="premium__plan-name">{PLAN_NAME[plan.key] || plan.key}</h2>
        <p className="premium__plan-price">
          {plan.upgrade && plan.list_minor > plan.price_minor ? (
            <span className="premium__plan-was">
              {euro(plan.list_minor, currency)}
            </span>
          ) : null}
          {euro(plan.price_minor, currency)}
          <span className="premium__plan-per">
            {" "}
            {intl.formatMessage({ id: "premium_per_year" })}
          </span>
        </p>
        <p className="premium__plan-tax">
          {intl.formatMessage({ id: "premium_tax_included" })}
        </p>
        {/* Dodatna (sidrena) cijena — mandatory next to every advertised price from 1.10.2026
            (NN 101/2026). Falls back to the list price if the API is older than this build. */}
        <p className="premium__plan-anchor">
          {intl.formatMessage(
            { id: "premium_anchor_price" },
            { price: euro(plan.anchor_minor ?? plan.list_minor, currency) }
          )}
        </p>
        {plan.upgrade && plan.list_minor > plan.price_minor ? (
          <p className="premium__plan-upgrade-note">
            {intl.formatMessage({ id: "premium_upgrade_note" })}
          </p>
        ) : null}
        <ul className="premium__plan-feats">
          {planFeatures(plan).map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
        <button
          type="button"
          className="btn-cta premium__cta"
          onClick={() => handleUpgrade(plan.key)}
          disabled={isLoading || !!startingPlan}
        >
          {intl.formatMessage({
            id:
              startingPlan === plan.key
                ? "premium_cta_loading"
                : isCurrent
                ? "premium_extend"
                : "premium_choose",
          })}
        </button>
      </article>
    );
  };

  return (
    <Layout>
      <div className="premium">
        <div className="container">
          <section className="premium__hero">
            <p className="premium__eyebrow">
              {intl.formatMessage({ id: "premium_eyebrow" })}
            </p>
            <h1 className="premium__title">
              {intl.formatMessage({ id: "premium_title" })}
            </h1>
            <p className="premium__lead">
              {intl.formatMessage({ id: "premium_subtitle" })}
            </p>
          </section>

          {isPremium ? (
            <div className="premium__active">
              <strong>
                {intl.formatMessage(
                  { id: "premium_active_until" },
                  {
                    date: status?.premium_until
                      ? formatDate(status.premium_until, locale)
                      : "",
                  }
                )}
              </strong>
            </div>
          ) : null}

          <section className="premium__code">
            <label className="premium__code-label" htmlFor="promo-code">
              {intl.formatMessage({ id: "premium_code_label" })}
            </label>
            <input
              id="promo-code"
              className="premium__code-input"
              placeholder={intl.formatMessage({ id: "premium_code_placeholder" })}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="off"
            />
          </section>

          <section className="premium__plans">
            {plans.map((plan) => (
              <PlanCard key={plan.key} plan={plan} />
            ))}
          </section>

          {(status?.enterprise_plans || []).length ? (
            <section className="premium__enterprise">
              <div className="premium__enterprise-head">
                <h2 className="premium__enterprise-title">
                  {intl.formatMessage({ id: "premium_enterprise_title" })}
                </h2>
                <p className="premium__enterprise-lead">
                  {intl.formatMessage({ id: "premium_enterprise_lead" })}
                </p>
              </div>
              <div className="premium__enterprise-grid">
                {status.enterprise_plans.map((plan) => {
                  const isCurrent = currentPlan === plan.key;
                  return (
                    <article className="premium__ent-card" key={plan.key}>
                      <h3 className="premium__ent-name">
                        {ENT_NAME[plan.key] || plan.key}
                      </h3>
                      <p className="premium__ent-price">
                        {euro(plan.price_minor, currency)}
                        <span className="premium__plan-per">
                          {" "}
                          {intl.formatMessage({ id: "premium_per_year" })}
                        </span>
                      </p>
                      <p className="premium__plan-tax">
                        {intl.formatMessage({ id: "premium_tax_included" })}
                      </p>
                      <p className="premium__plan-anchor">
                        {intl.formatMessage(
                          { id: "premium_anchor_price" },
                          {
                            price: euro(
                              plan.anchor_minor ?? plan.list_minor,
                              currency
                            ),
                          }
                        )}
                      </p>
                      <ul className="premium__plan-feats">
                        {entFeatures(plan).map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        className="btn-cta premium__cta"
                        onClick={() => handleUpgrade(plan.key)}
                        disabled={isLoading || !!startingPlan}
                      >
                        {intl.formatMessage({
                          id:
                            startingPlan === plan.key
                              ? "premium_cta_loading"
                              : isCurrent
                              ? "premium_extend"
                              : "premium_choose",
                        })}
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>
          ) : null}

          {isPremium && status?.sms ? (
            <section className="premium__sms">
              <h2 className="premium__sms-title">
                {intl.formatMessage({ id: "sms_section_title" })}
              </h2>
              <p className="premium__sms-usage">
                {status.sms.included >= 100000
                  ? intl.formatMessage(
                      { id: "sms_usage_unlimited" },
                      { used: status.sms.used }
                    )
                  : intl.formatMessage(
                      { id: "sms_usage" },
                      { used: status.sms.used, included: status.sms.included }
                    )}
                {status.sms.credits > 0 ? (
                  <>
                    {" · "}
                    {intl.formatMessage(
                      { id: "sms_credits_balance" },
                      { n: status.sms.credits }
                    )}
                  </>
                ) : null}
              </p>
              <div className="premium__sms-buy">
                <input
                  type="number"
                  min="1"
                  max="1000"
                  className="premium__sms-qty"
                  value={creditQty}
                  onChange={(e) => setCreditQty(e.target.value)}
                />
                <button
                  type="button"
                  className="btn-cta premium__sms-cta"
                  onClick={buyCredits}
                  disabled={buyingCredits}
                >
                  {intl.formatMessage(
                    { id: buyingCredits ? "premium_cta_loading" : "sms_buy_cta" },
                    {
                      n: Math.max(1, Math.min(parseInt(creditQty, 10) || 0, 1000)),
                      price: euro(
                        Math.max(1, Math.min(parseInt(creditQty, 10) || 0, 1000)) *
                          (status.sms.credit_price_minor || 19),
                        currency
                      ),
                    }
                  )}
                </button>
              </div>
              <p className="premium__sms-hint">
                {intl.formatMessage(
                  { id: "sms_buy_hint" },
                  { price: euro(status.sms.credit_price_minor || 19, currency) }
                )}
              </p>
              <p className="premium__plan-anchor premium__sms-anchor">
                {intl.formatMessage(
                  { id: "premium_anchor_price" },
                  {
                    price: euro(
                      status.sms.credit_anchor_minor ??
                        status.sms.credit_price_minor ??
                        19,
                      currency
                    ),
                  }
                )}
              </p>
            </section>
          ) : null}

          <p className="premium__note">
            {intl.formatMessage({ id: "premium_secure_note" })}
          </p>
          {/* NN 101/2026: explain the sidrena cijena and link the published machine-readable
              cjenik (.csv/.xml lives on /cjenik). */}
          <p className="premium__note premium__note--legal">
            {intl.formatMessage({ id: "premium_anchor_note" })}{" "}
            <a href="/cjenik">
              {intl.formatMessage({ id: "premium_cjenik_link" })}
            </a>
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Premium;
